import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "./common/SafeIcon";
import { useFeatures } from "./hooks/useFeatures";
import { useLocalStorage } from "./hooks/useLocalStorage";
import TabNavigation from "./components/TabNavigation";
import FeatureList from "./components/FeatureList";
import PriorityFilter from "./components/PriorityFilter";
import SortFilter from "./components/SortFilter";
import SuggestFeatureModal from "./components/SuggestFeatureModal";
import { STATUS_TABS, statusMatchesTab, getTabLabel } from "./lib/statusConfig";
import { getEffectiveTime } from "./lib/dateUtils";

const { FiPlus, FiSearch } = FiIcons;

function App() {
  const {
    features,
    loading,
    error,
    createFeature,
    upvoteFeature,
  } = useFeatures();
  // Default landing tab is "All". The key is versioned (_v2) so visitors who
  // already had the old "not_started" default persisted from a previous visit
  // start on "All" as well, instead of being stuck on Requested forever.
  const [activeTab, setActiveTab] = useLocalStorage("roadmap_activeTab_v2", "all");
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [sortOrder, setSortOrder] = useLocalStorage("roadmap_sortOrder", "newest"); // default: newest on top, on every tab, on load
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Chronological sort, direction controlled by sortOrder ("newest" first by
  // default). Sorts by updated_at, falling back to created_at only when
  // updated_at is missing/invalid. The card display (FeatureCard) uses this
  // exact same helper, so the date you see always matches what determined
  // the item's position in the list.
  const sortedFeatures = useMemo(() => {
    const direction = sortOrder === "oldest" ? -1 : 1;
    return [...features].sort((a, b) => direction * (getEffectiveTime(b) - getEffectiveTime(a)));
  }, [features, sortOrder]);

  const searchedFeatures = useMemo(() => {
    if (!searchQuery.trim()) return sortedFeatures;
    const q = searchQuery.toLowerCase();
    const scored = sortedFeatures
      .map((f) => {
        const title = f.feature?.toLowerCase() || "";
        const desc = f.description?.toLowerCase() || "";
        const cat = f.category?.toLowerCase() || "";
        let score = 0;
        if (title === q) score = 4;
        else if (title.startsWith(q)) score = 3;
        else if (title.includes(q)) score = 2;
        else if (desc.includes(q) || cat.includes(q)) score = 1;
        return { ...f, _score: score };
      })
      .filter((f) => f._score > 0)
      .sort((a, b) => b._score - a._score);
    return scored;
  }, [sortedFeatures, searchQuery]);

  const tabs = useMemo(() => {
    let filteredFeatures = searchedFeatures;
    if (selectedPriorities.length > 0) {
      filteredFeatures = filteredFeatures.filter((f) =>
        selectedPriorities.includes(f.priority?.toLowerCase() || "medium")
      );
    }
    return STATUS_TABS.map((tab) => ({
      id: tab.id,
      label: tab.label,
      count: filteredFeatures.filter((f) => statusMatchesTab(f.status, tab.id)).length,
    }));
  }, [searchedFeatures, selectedPriorities]);

  const currentFeatures = useMemo(() => {
    let filtered = searchedFeatures;
    if (selectedPriorities.length > 0) {
      filtered = filtered.filter((f) =>
        selectedPriorities.includes(f.priority?.toLowerCase() || "medium")
      );
    }
    if (activeTab !== "all") {
      filtered = filtered.filter((f) => statusMatchesTab(f.status, activeTab));
    }
    return filtered;
  }, [searchedFeatures, activeTab, selectedPriorities]);

  // Empty-state copy. The "All" tab takes no adjective ("No features yet"),
  // and the other tabs use their display label rather than the raw tab id.
  const emptyMessage = useMemo(() => {
    if (features.length === 0) return "No features found.";
    const scope = activeTab === "all" ? "" : `${getTabLabel(activeTab).toLowerCase()} `;
    return selectedPriorities.length > 0
      ? `No ${scope}features match the selected priority filters`
      : `No ${scope}features yet`;
  }, [features.length, activeTab, selectedPriorities.length]);

  const handleFeatureRequestSubmitted = async (featureData) => {
    try {
      await createFeature(featureData);
      setSuccessMessage(`"${featureData.feature}" has been added to the roadmap!`);
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Failed to add feature:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d0d10" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Loading features...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#0d0d10" }}>
      <div className="fixed left-[-200px] top-[-200px] w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(109,40,217,0.18) 0%, transparent 70%)", filter: "blur(40px)" }}
      />
      <div className="fixed right-[-200px] bottom-[-100px] w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.20) 0%, transparent 70%)", filter: "blur(60px)" }}
      />
      <div className="fixed left-1/2 bottom-0 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center bottom, rgba(124,58,237,0.12) 0%, transparent 70%)", filter: "blur(30px)" }}
      />

      <div className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="w-full max-w-[1137px] h-auto opacity-100 flex flex-col gap-3 mb-6 px-4 sm:px-0">
            <div className="flex flex-col items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="w-full h-auto opacity-100"
              >
                <h1 className="font-figtree font-bold text-2xl sm:text-3xl md:text-[40px] leading-tight md:leading-[48px] tracking-tight text-center">
                  <span className="text-white">Greta LTD </span>
                  <span className="bg-gradient-to-r from-[#CB6DDC] via-[#7E40F1] to-[#4685FA] text-transparent bg-clip-text">Roadmap</span>
                </h1>
              </motion.div>

              <p className="font-figtree font-normal text-sm sm:text-base md:text-[16px] leading-normal md:leading-[24px] tracking-normal text-center text-gray-400 mt-2 max-w-[90%] md:max-w-full">
                Stay updated on the latest feature developments from ideas to completed milestones.
              </p>
            </div>

            <div className="w-full h-px mt-2" style={{ background: "rgba(255,255,255,0.07)" }}></div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl text-sm text-green-300"
              style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.20)" }}
            >
              {successMessage}
            </motion.div>
          )}

          {/* Error Banner */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl"
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}
            >
              <h3 className="font-medium text-red-400 mb-1 text-sm">Connection Error</h3>
              <p className="text-red-400/70 text-xs">{error}</p>
            </motion.div>
          )}

          {/* Search Bar */}
          <div className="flex items-center gap-2 mb-6 max-w-xl">
            <div className="relative flex-1">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setSearchQuery("")}
                placeholder="Search features by name, description, or category..."
                className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm text-white placeholder-gray-500 outline-none transition-all focus:ring-1 focus:ring-violet-500"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-xs font-medium"
                >
                  ✕
                </button>
              )}
            </div>
            <button
              onClick={() => setActiveTab("all")}
              className="flex items-center gap-1.5 px-4 py-2.5 active:scale-[0.97] text-white text-sm font-medium rounded-lg transition-all whitespace-nowrap"
              style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow: "0 0 20px rgba(124,58,237,0.30)" }}
            >
              <SafeIcon icon={FiSearch} className="w-3.5 h-3.5" />
              Search
            </button>
          </div>

          {/* Navigation Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            <div className="flex-1 max-w-full lg:max-w-4xl">
              <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-4 sm:mt-0">
              <div className="w-full sm:w-auto sm:flex-shrink-0">
                <SortFilter sortOrder={sortOrder} onSortChange={setSortOrder} />
              </div>
              <div className="w-full sm:w-auto sm:flex-shrink-0">
                <PriorityFilter
                  selectedPriorities={selectedPriorities}
                  onPriorityChange={setSelectedPriorities}
                  onClearFilters={() => setSelectedPriorities([])}
                />
              </div>
              <button
                onClick={() => setShowSuggestModal(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 sm:py-1.5 text-white rounded-lg transition-all active:scale-[0.97] shadow-lg w-auto self-start whitespace-nowrap"
                style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)", boxShadow: "0 0 20px rgba(124,58,237,0.25)" }}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span className="text-sm">Request Feature</span>
              </button>
            </div>
          </div>

          {/* Feature List */}
          <motion.div
            key={`${activeTab}-${selectedPriorities.join("-")}-${sortOrder}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            <FeatureList
              features={currentFeatures}
              onUpvote={upvoteFeature}
              isAdmin={false}
              emptyMessage={emptyMessage}
            />
          </motion.div>

          <SuggestFeatureModal
            isOpen={showSuggestModal}
            onClose={() => setShowSuggestModal(false)}
            onSubmit={handleFeatureRequestSubmitted}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
